import { useCallback, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import { type Address } from 'viem';
import { useShallow } from 'zustand/react/shallow';

import { useToastStore } from '@pkg/components/Toast/useToastStore';
import { type Chain, type FeasibilityResponse, type NewOrder, type NewOrderItem } from '@pkg/graphql/codegen/graphql';
import { useExecuteStep } from '@pkg/hooks/marketplace/useExecuteStep';
import { useSubmitAptosTransaction } from '@pkg/hooks/marketplace/useSubmitTransaction';
import { parseOriginError, parsePrepareError } from '@pkg/hooks/marketplace/utils';
import { useQueryInvalidation } from '@pkg/hooks/reactQuery';
import { useWalletConnections } from '@pkg/hooks/wallets';
import { convertEthToWei, getErrorMessage } from '@pkg/utils';

import { callPrepareOrderMutation } from '@app/client/resources/marketplace';
import { CURRENT_USER_ORDERS_KEY } from '@app/pages/ProfilePage/hooks/useOrders';
import { useOrdersStore } from '@app/store/zustand';

export type PrepareOrderType = 'list' | 'buy' | 'offer' | 'sell';

export type UsePrepareOrderArgs = {
    allMarkets?: boolean;
    confirmedPrice?: number;
    expiration?: string;
};
export function usePrepareOrder() {
    const invalidateOrders = useQueryInvalidation([CURRENT_USER_ORDERS_KEY]);
    const { connectedAccount, getConnectedAccount } = useWalletConnections();

    const { addToast } = useToastStore();
    const executeStep = useExecuteStep();
    const handleAptosOrder = useSubmitAptosTransaction();
    const { listingOrderRequest, addConfirmedToken, token, reset, setOrderRequestPendingId } = useOrdersStore(
        useShallow((s) => s),
    );
    const { mutateAsync: prepareOrderMutation, isPending } = useMutation({
        mutationFn: (request: NewOrder) => callPrepareOrderMutation({ variables: { request } }),
    });
    useEffect(
        () => setOrderRequestPendingId(isPending ? token?.id : undefined),
        [isPending, setOrderRequestPendingId, token?.id],
    );
    const handleFail = useCallback(
        (e?: string) => {
            const message = e || 'Something went wrong. Please try again.';
            addToast({ message, minimal: true, timeout: 3000 });
            reset();
        },
        [addToast, reset],
    );

    const confirmAccount = useCallback(
        async (itemChain: string, itemAccountAddress?: string) => {
            if (itemAccountAddress) {
                // needs to be different depending on SIDE
                const itemAccountIsConnected = connectedAccount?.toLowerCase() === itemAccountAddress.toLowerCase();
                if (!itemAccountIsConnected) {
                    return await getConnectedAccount(`${itemChain}.${itemAccountAddress}`);
                } else {
                    return connectedAccount;
                }
            } else {
                return await getConnectedAccount(`${itemChain}.${connectedAccount}`);
            }
        },
        [connectedAccount, getConnectedAccount],
    );

    const handlePrepareResponse = useCallback(
        async (prepareRes: FeasibilityResponse, account: Address, chain?: string) => {
            if (chain && chain.toLowerCase() === 'aptos') {
                const res = await handleAptosOrder(prepareRes, account);
                if (res && token) {
                    addConfirmedToken(token);
                    invalidateOrders();
                } else {
                    handleFail('Failed to submit order.');
                }
            } else if (prepareRes && account) {
                try {
                    const res = await executeStep(account, prepareRes);
                    if (res && token) {
                        addConfirmedToken(token);
                        invalidateOrders();
                    } else {
                        handleFail('Failed to execute order steps.');
                    }
                } catch (_e) {
                    handleFail();
                }
            }
        },
        [addConfirmedToken, executeStep, handleAptosOrder, handleFail, invalidateOrders, token],
    );

    const prepareOrder = useCallback(
        async (args?: UsePrepareOrderArgs) => {
            const { confirmedPrice, expiration, allMarkets = false } = args || {};
            try {
                const itemPrice = confirmedPrice ? convertEthToWei(confirmedPrice || 0).toString() : undefined;
                const items = listingOrderRequest?.items;
                const itemAccount = listingOrderRequest?.account;
                const [itemChain, itemAccountAddress] = itemAccount?.split('.') || [];
                const chain = itemChain || listingOrderRequest?.chain?.toLowerCase();
                const correctAccount = await confirmAccount(chain, itemAccountAddress);

                const orderItems = items?.map((item) => ({
                    ...item,
                    quantity: item.quantity ?? 1,
                    price: itemPrice,
                })) as NewOrderItem[];
                try {
                    if (correctAccount) {
                        const request = {
                            ...listingOrderRequest,
                            items: orderItems,
                            expirationTimestamp: expiration,
                            chain: listingOrderRequest?.chain?.toUpperCase() as Chain,
                            allMarkets,
                        } as NewOrder;
                        try {
                            const prepareRes = await prepareOrderMutation({
                                ...request,
                                account: `${chain}.${correctAccount}`,
                            });
                            if (prepareRes && prepareRes.prepare) {
                                if (prepareRes.prepare.origin.errors?.length) {
                                    const errorMessage = parseOriginError(prepareRes?.prepare?.origin?.errors?.[0]);
                                    handleFail(errorMessage);
                                } else {
                                    return await handlePrepareResponse(
                                        prepareRes.prepare,
                                        correctAccount as Address,
                                        chain,
                                    );
                                }
                            }
                        } catch (e) {
                            const errorMessage = parsePrepareError(e);
                            handleFail(errorMessage);
                        }
                    }
                } catch (e) {
                    const errorMessage = getErrorMessage(e);
                    handleFail(errorMessage);
                }
            } catch (e) {
                const errorMessage = getErrorMessage(e);
                handleFail(errorMessage);
                return e;
            }
        },
        [confirmAccount, handleFail, handlePrepareResponse, listingOrderRequest, prepareOrderMutation],
    );

    return { prepareOrder };
}
